import React, { useState } from 'react';
import { fmt } from './fmt';

/**
 * SVG trend chart — ported from AI-ManagementBoard_UI案/components.jsx
 * Hover shows 実績 / 予測 / 目標達成に必要 / 目標 for the nearest month.
 */
export default function LineChart({ metric, months }) {
  const [hoverIdx, setHoverIdx] = useState(null);

  if (!metric || !months?.length) {
    return (
      <div className="chart-empty" style={{ padding: '24px 0', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>
        データがありません
      </div>
    );
  }

  const W = 320;
  const H = 290;
  const padL = 42;
  const padR = 10;
  const padT = 24;
  const padB = 24;

  const actual = metric.actual || [];
  const forecast = metric.forecast || [];
  const target = metric.target;
  const requiredSeries = Array.isArray(metric.required) ? metric.required : null;

  const numeric = [
    ...actual.filter((v) => v != null),
    ...forecast.filter((v) => v != null),
    ...(target != null ? [target] : []),
    ...(requiredSeries ? requiredSeries.filter((v) => v != null) : []),
  ];

  if (numeric.length === 0) {
    return (
      <div className="chart-empty" style={{ padding: '24px 0', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>
        データがありません
      </div>
    );
  }

  const dataMin = Math.min(...numeric);
  const dataMax = Math.max(...numeric);
  const maxAbs = Math.max(...numeric.map((v) => Math.abs(v)));
  // Flat series → tiny span would look like noise; keep a readable band.
  const span = dataMax - dataMin || Math.abs(dataMax) * 0.1 || 1;
  let lo = dataMin - span * 0.12;
  let hi = dataMax + span * 0.12;
  // These KPIs are never negative — don't invent a negative axis (e.g. ¥-1.7千).
  if (dataMin >= 0 && lo < 0) lo = 0;
  // 客数系は整数の世界: snap the domain to whole units and a span divisible by the
  // 4 tick intervals, so the axis can never label a fractional person (31.8名).
  if (metric.unit === 'count' || metric.unit === 'pieces') {
    lo = Math.max(0, Math.floor(lo));
    hi = Math.ceil(hi);
    while ((hi - lo) % 4 !== 0) hi += 1;
  }
  const n = months.length;
  const x = (i) => padL + (i / Math.max(n - 1, 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);

  const axfmt = (v) => {
    if (metric.unit === 'currency') {
      // Scale off plotted data, not `target` — target is often null, and
      // `null >= 100000` is false, which pinned every currency chart to 千.
      return maxAbs >= 100000
        ? `¥${Math.round(v / 10000)}万`
        : `¥${((Math.round(v / 100) * 100) / 1000).toFixed(1)}千`;
    }
    if (metric.unit === 'percent') return `${Math.round(v * 10) / 10}%`;
    if (metric.unit === 'count') return `${Math.round(v)}名`;
    if (metric.unit === 'pieces') return `${Math.round(v)}件`;
    // 1 decimal: the interval spans ~1.5日 on this chart, so whole-day rounding
    // printed the same 52日 on three neighbouring ticks.
    if (metric.unit === 'days') return `${Math.round(v * 10) / 10}日`;
    return String(Math.round(v));
  };

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((g) => ({
    g,
    v: hi - g * (hi - lo),
    yy: padT + g * (H - padT - padB),
  }));

  const toPts = (arr) => arr.map((v, i) => (v == null ? null : [x(i), y(v)]));

  /**
   * Split on nulls instead of dropping them: a missing month must show as a
   * break. Filtering nulls out joined the survivors with a straight segment,
   * which drew an interpolated guess as if it were measured data.
   */
  const toSegments = (arr) => {
    const segs = [];
    let cur = [];
    toPts(arr).forEach((p) => {
      if (!p) {
        if (cur.length > 1) segs.push(cur);
        cur = [];
        return;
      }
      cur.push(p);
    });
    if (cur.length > 1) segs.push(cur);
    return segs;
  };

  const actPts = toPts(actual).filter(Boolean);
  const actSegs = toSegments(actual);
  const fcSegs = toSegments(forecast);

  let lastActualIdx = -1;
  for (let i = actual.length - 1; i >= 0; i -= 1) {
    if (actual[i] != null) {
      lastActualIdx = i;
      break;
    }
  }
  const useRequiredSeries = requiredSeries != null && requiredSeries.length === n;

  // Only the adapter-computed series. There used to be a fallback here that drew a
  // straight 2-point segment from the last 実績 to 目標 at the chart's right edge — it
  // ran whenever `metric.required` was missing, which made every failure upstream look
  // like a confident, perfectly linear plan. It ignored 予測 entirely, so no fix to the
  // trajectory maths could ever change the line. No series → no gold line.
  const reqSegs = useRequiredSeries ? toSegments(requiredSeries) : [];

  const requiredAt = (i) => {
    if (!useRequiredSeries) return null;
    const v = requiredSeries[i];
    return v != null && !Number.isNaN(v) ? v : null;
  };

  const deadlineIdx = metric.requiredMeta?.deadlineIdx ?? -1;
  const aheadOfTarget = useRequiredSeries && metric.requiredMeta?.aheadOfTarget === true;
  const endDot =
    useRequiredSeries && deadlineIdx >= 0 && deadlineIdx < n && requiredSeries[deadlineIdx] != null
      ? { cx: x(deadlineIdx), cy: y(requiredSeries[deadlineIdx]) }
      : null;

  const toPath = (pts) =>
    pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const tgtY = target != null ? y(target) : null;
  const tgtLabelY = tgtY != null ? (tgtY < padT + 16 ? tgtY + 13 : tgtY - 6) : 0;
  const halo = { stroke: 'var(--bg-card)', strokeWidth: 3.2, strokeLinejoin: 'round', paintOrder: 'stroke' };

  const colHalf = n > 1 ? (W - padL - padR) / (n - 1) / 2 : (W - padL - padR) / 2;

  const pickHoverIdx = (clientX, svgEl) => {
    if (!svgEl) return;
    const rect = svgEl.getBoundingClientRect();
    if (!rect.width) return;
    const sx = ((clientX - rect.left) / rect.width) * W;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < n; i += 1) {
      const d = Math.abs(sx - x(i));
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setHoverIdx(best);
  };

  const tipRows = [];
  if (hoverIdx != null) {
    const i = hoverIdx;
    const act = actual[i];
    const fc = forecast[i];
    const need = requiredAt(i);
    if (act != null) tipRows.push({ key: 'actual', label: '実績', value: act, tone: 'actual' });
    if (fc != null && (act == null || i > lastActualIdx || (i === lastActualIdx && fc !== act))) {
      tipRows.push({ key: 'forecast', label: '現状の予測', value: fc, tone: 'forecast' });
    }
    if (need != null) {
      // 予測 already clears 目標 by the deadline, so 必要 sits below 予測 — that is slack, not a cut
      tipRows.push({
        key: 'need',
        label: aheadOfTarget ? '目標達成に必要（達成見込み）' : '目標達成に必要',
        value: need,
        tone: 'need',
      });
    }
    if (target != null) tipRows.push({ key: 'target', label: '目標', value: target, tone: 'target' });
  }

  const tipLeftPct = hoverIdx != null ? (x(hoverIdx) / W) * 100 : 0;
  const tipFlip = tipLeftPct > 62;

  return (
    <div className="chart-wrap" onMouseLeave={() => setHoverIdx(null)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="chart-svg"
        style={{ width: '100%', height: 'auto', display: 'block' }}
        onMouseMove={(e) => pickHoverIdx(e.clientX, e.currentTarget)}
        onTouchStart={(e) => {
          const t = e.touches[0];
          if (t) pickHoverIdx(t.clientX, e.currentTarget);
        }}
        onTouchMove={(e) => {
          const t = e.touches[0];
          if (t) pickHoverIdx(t.clientX, e.currentTarget);
        }}
      >
        {ticks.map((t, i) => (
          <line
            key={i}
            x1={padL}
            x2={W - padR}
            y1={t.yy}
            y2={t.yy}
            stroke="var(--line)"
            strokeWidth={t.g === 0 || t.g === 1 ? '1.2' : '1'}
          />
        ))}
        {tgtY != null && (
          <line x1={padL} x2={W - padR} y1={tgtY} y2={tgtY} stroke="var(--gold)" strokeWidth="1.5" strokeDasharray="3 3" />
        )}
        {reqSegs.map((seg, i) => (
          <path
            key={`req${i}`}
            d={toPath(seg)}
            fill="none"
            stroke="var(--gold)"
            strokeWidth="2"
            strokeDasharray="2 4"
            strokeLinecap="round"
            opacity="0.95"
          />
        ))}
        {fcSegs.map((seg, i) => (
          <path
            key={`fc${i}`}
            d={toPath(seg)}
            fill="none"
            stroke="var(--brand-deep)"
            strokeWidth="2"
            strokeDasharray="5 4"
            opacity="0.85"
          />
        ))}
        {actSegs.map((seg, i) => (
          <path
            key={`act${i}`}
            d={toPath(seg)}
            fill="none"
            stroke="var(--brand)"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {actPts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="2.6" fill="var(--bg-card)" stroke="var(--brand)" strokeWidth="2" />
        ))}
        {forecast.map((v, i) =>
          v != null && i > lastActualIdx ? (
            <circle key={`f${i}`} cx={x(i)} cy={y(v)} r="2.4" fill="var(--brand-deep)" />
          ) : null
        )}
        {endDot && (
          <circle cx={endDot.cx} cy={endDot.cy} r="3" fill="var(--gold)" stroke="var(--bg-card)" strokeWidth="1.5" />
        )}
        {ticks.map((t, i) => (
          <text
            key={`yl${i}`}
            x={padL - 7}
            y={t.yy}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize="8.5"
            fontWeight="700"
            fill="var(--muted)"
            {...halo}
          >
            {axfmt(t.v)}
          </text>
        ))}
        {months.map((m, i) =>
          i % 2 === 0 || i === n - 1 ? (
            <text
              key={`xl${i}`}
              x={x(i)}
              y={H - 6}
              textAnchor="middle"
              fontSize="9"
              fontWeight="600"
              fill="var(--muted)"
              {...halo}
            >
              {m}
            </text>
          ) : null
        )}
        {target != null && (
          <text x={W - padR} y={tgtLabelY} textAnchor="end" fontSize="9" fontWeight="800" fill="var(--gold)" {...halo}>
            目標 {fmt(metric.unit, metric.target)}
          </text>
        )}
        {hoverIdx != null && (
          <line
            x1={x(hoverIdx)}
            x2={x(hoverIdx)}
            y1={padT}
            y2={H - padB}
            stroke="var(--slate)"
            strokeWidth="1"
            strokeDasharray="2 3"
            opacity="0.55"
            pointerEvents="none"
          />
        )}
        {months.map((_, i) => (
          <rect
            key={`hit${i}`}
            x={Math.max(padL, x(i) - colHalf)}
            y={padT}
            width={Math.min(colHalf * 2, W - padR - Math.max(padL, x(i) - colHalf))}
            height={H - padT - padB}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
          />
        ))}
      </svg>
      {hoverIdx != null && tipRows.length > 0 && (
        <div
          className={`chart-tooltip${tipFlip ? ' chart-tooltip--flip' : ''}`}
          style={{ left: `${tipLeftPct}%` }}
        >
          <div className="chart-tooltip-month">{months[hoverIdx]}</div>
          {tipRows.map((row) => (
            <div key={row.key} className={`chart-tooltip-row chart-tooltip-row--${row.tone}`}>
              <span className="chart-tooltip-k">{row.label}</span>
              <span className="chart-tooltip-v">{fmt(metric.unit, row.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
